import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormContainer } from '@shared/classes/form-container';
import { Subscription } from 'rxjs';
import { DeliveryCostCheckPopupComponent } from '../delivery-cost-check-popup/delivery-cost-check-popup.component';
import { redistributePlacesCost } from '@shared/components/delivery-cost/delivery-cost.helpers';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-afterpayment',
  templateUrl: './afterpayment.component.html',
  styleUrls: ['./afterpayment.component.scss'],
})
export class AfterpaymentComponent extends FormContainer implements OnInit, OnDestroy {
  readonly maxAfterpaymentCost = 399999;
  @Input() controlName = 'AfterpaymentOnGoodsCost';
  @Input() invoiceId: string;
  @Input() showTooltipAfterpayment: boolean;

  afterpaymentGroup: UntypedFormGroup;

  costSubscription: Subscription;

  targetValue: number;

  previousValue: number;

  @Input()
  isChangeEw = false;

  constructor(protected formBuilder: UntypedFormBuilder, public dialog: MatDialog) {
    super();
  }

  get stepAddressControl(): AbstractControl {
    return this.form.parent.get('stepAddressForm');
  }

  get AdditionalServices(): AbstractControl {
    return this.stepAddressControl.get('AdditionalServices');
  }

  get costControl(): AbstractControl {
    return this.isChangeEw ? this.form.get('Cost') : this.form?.parent?.get('stepDeliveryForm')?.get('Cost');
  }

  get costValue(): number {
    return !isNaN(this.costControl?.value) ? this.costControl.value : 0;
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onFocus(): void {
    this.previousValue = this.afterpaymentGroup.get(this.controlName).value;
  }

  change(event) {
    this.targetValue = event.target.value.replace(/,/gi, '.');
    if (+this.targetValue > +this.costValue) {
      this.openDialog(this.targetValue);
    }
  }

  openDialog(value) {
    const dialogRef: MatDialogRef<DeliveryCostCheckPopupComponent> = this.dialog.open(DeliveryCostCheckPopupComponent, {
      panelClass: 'no-padding',
    });
    dialogRef.disableClose = true;
    const previousCost = this.costValue;
    dialogRef.componentInstance.cost = value;
    dialogRef.componentInstance.announcedCost = previousCost;
    dialogRef.componentInstance.type = this.controlName;

    this.subscriptions = dialogRef.componentInstance.onAdd.subscribe(() => {
      if (+value > previousCost) {
        const stepDeliveryForm = this.form.parent.get('stepDeliveryForm') as UntypedFormGroup;
        stepDeliveryForm.get('Cost').setValue(value);
        stepDeliveryForm.get('Cost').updateValueAndValidity({ onlySelf: true });
        redistributePlacesCost(stepDeliveryForm, previousCost, value);
      }
    });

    this.subscriptions = dialogRef.componentInstance.onClear.subscribe(() => {
      this.afterpaymentGroup.get(this.controlName).setValue(this.previousValue);
      this.afterpaymentGroup.get(this.controlName).updateValueAndValidity({ onlySelf: true });
    });

    dialogRef.afterClosed();
  }

  private getCostValue(): number {
    return this.costControl ? Math.min(this.costValue, this.maxAfterpaymentCost) : 0;
  }

  addFormControls() {
    this.afterpaymentGroup = this.form.get('AdditionalServices').get('afterpayment') as UntypedFormGroup;
    const cost = this.costValue;

    this.afterpaymentGroup.addControl(
      this.controlName,
      new UntypedFormControl(
        this.getCostValue(),
        Validators.compose([Validators.required, Validators.min(0), Validators.max(this.maxAfterpaymentCost)]),
      ),
    );
  }

  ngOnDestroy() {
    this.afterpaymentGroup.removeControl(this.controlName);
    if (this.costSubscription) {
      this.costSubscription.unsubscribe();
    }
  }
}
